import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DataRoomBuySideGroup } from 'router/subrouters/DataRoom/models/DataRoomMember';
import { getArrayLength, getUserFullName, normalizeText } from 'utils';
import { TRANSITION_TIME } from 'data/global';

// A group created for a single member is named after that member, so the name
// stops describing the group as soon as company arrives — however many members
// join at once. A group that already held two or more has outgrown its name
// long ago, and the nudge has had its chance.
const SUGGEST_AFTER_MEMBER_COUNT = 1;

const getGroupMemberIds = (groups: DataRoomBuySideGroup[]) =>
  groups.reduce<Record<string, string[]>>(
    (acc, { _id, dataRoomMembers }) => ({
      ...acc,
      [_id]: dataRoomMembers.map(({ _id: memberId }) => memberId),
    }),
    {},
  );

// Nothing on the group records whether the broker has renamed it, so the name
// itself is the signal: a name that still matches one of the members is the
// auto-generated one (or one that reads exactly like it), while anything else
// is a name somebody chose on purpose.
const getHasMemberName = ({ name, dataRoomMembers }: DataRoomBuySideGroup) => {
  const groupName = normalizeText(name);

  if (!groupName) return false;

  return dataRoomMembers.some(({ firstName, lastName }) => {
    const fullName = getUserFullName(firstName, lastName).toLowerCase();

    return !!fullName && fullName === groupName;
  });
};

// Suggests renaming a buy-side group as soon as a one-member group takes on
// company while the broker is looking at the table. The suggestion is
// deliberately not persisted: it belongs to the moment the members were added,
// so dismissing it, reloading, or coming back later all leave it behind.
export default function useRenameGroupSuggestion(
  buySideGroups: DataRoomBuySideGroup[],
  isEnabled = true,
) {
  const [suggestedGroupId, setSuggestedGroupId] = useState<string>();

  // What each group held on the previous update, so a group that has just
  // grown past its single member can be told apart from one that was already
  // bigger than that.
  const groupMemberIdsRef = useRef<Record<string, string[]>>();

  const showTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const groupMemberIds = getGroupMemberIds(buySideGroups);
    const previousGroupMemberIds = groupMemberIdsRef.current;

    groupMemberIdsRef.current = groupMemberIds;

    // The first pass only records where every group started: the nudge belongs
    // to a member added while the table was open, not to what was already
    // there when it loaded.
    if (!previousGroupMemberIds || !isEnabled) return;

    // A merge produces a group that was not there before, so it has no
    // previous size and never suggests a rename.
    const grownGroup = buySideGroups.find(
      ({ _id, dataRoomMembers }) =>
        getArrayLength(previousGroupMemberIds[_id]) ===
          SUGGEST_AFTER_MEMBER_COUNT &&
        getArrayLength(dataRoomMembers) > SUGGEST_AFTER_MEMBER_COUNT,
    );

    if (!grownGroup || !getHasMemberName(grownGroup)) return;

    // The members list updates while the drawer the broker added from is still
    // sliding shut, so the nudge waits for it to be out of the way.
    clearTimeout(showTimeoutRef.current);

    showTimeoutRef.current = setTimeout(
      () => setSuggestedGroupId(grownGroup._id),
      TRANSITION_TIME,
    );
  }, [buySideGroups, isEnabled]);

  useEffect(() => () => clearTimeout(showTimeoutRef.current), []);

  const dismissRenameSuggestion = useCallback(() => {
    clearTimeout(showTimeoutRef.current);
    setSuggestedGroupId(undefined);
  }, []);

  // Nothing is stored, so the suggestion simply stops applying once the group
  // is renamed, deleted, or back down to the one member it was named after.
  const renameSuggestionGroupId = useMemo(() => {
    if (!suggestedGroupId) return undefined;

    const group = buySideGroups.find(({ _id }) => _id === suggestedGroupId);

    const isStillRelevant =
      !!group &&
      getArrayLength(group.dataRoomMembers) > SUGGEST_AFTER_MEMBER_COUNT &&
      getHasMemberName(group);

    return isStillRelevant ? suggestedGroupId : undefined;
  }, [buySideGroups, suggestedGroupId]);

  return { renameSuggestionGroupId, dismissRenameSuggestion };
}
